import { CheckGroupProps } from 'components/elements/form/CheckGroup'
import { CombinationGroupProps } from 'components/elements/form/CombinationGroup'
import { DivideCarFourGroupProps } from 'components/elements/form/DamagedPartGroup/DivideCarFourGroup'
import { RadioGroupProps } from 'components/elements/form/RadioGroup'
import { SelectImageGroupProps } from 'components/elements/form/SelectImageGroup'
import { SwitchGroupProps } from 'components/elements/form/SwitchGroup'
import { TextAreaGroupProps } from 'components/elements/form/TextAreaGroup'
import { MODAL_DISPLAY_TIME } from 'components/elements/modal/AnimationModal'
import { API } from 'constants/api'
import { PAGE_PATH } from 'constants/pagePath'
import { QN_STATUS } from 'constants/qnStatus'
import { APIKeyContext } from 'contexts/APIKeyContext'
import {
  EditingItemContext,
  EditingItemContextProps,
} from 'contexts/EditingItemContext'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import { JwtContext, JwtContextProps } from 'contexts/JwtContext'
import dayjs from 'dayjs'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  Dispatch,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  ContractInfo,
  initialQnEditState,
  LocationStateInfo,
  QnEditStateProps,
  RelationshipTypes,
  VehicleStateInfo,
  VehicleTypes,
} from 'states/QnState'
import { QnFrontVersion } from 'states/versionControl'
import deepcopy from 'ts-deepcopy'
import { useSender } from 'utils/axios'
import {
  convertFullKatakana,
  splitFullWidthPlateNumber,
  sepalatePhoneNumber,
  splitFullWidthName,
  transitionWithAction,
} from 'utils/commonUtils'
import { localStorageAvailable } from 'utils/localStorage'
import { StatusProps, success } from 'utils/status'
import { useStartWebSocket, useExitWebSocket } from './websocket'
import DOMAIN from 'constants/domains'
import {
  getDomain,
  isKsDomain,
  getKsClientLowerCase,
  isSaficDomain,
} from './domain'
import { URLParameters, QnState, QnStatesProps } from '../states/QnState'
import { CLIENT_SETTINGS } from 'constants/clientSettings'

/**
 * localStorage用にラップしたStateのHooksを返却する
 * @param key localStorageで用いる一意キー
 * @param initialState 初期State
 * @returns StateのHooks
 */
export const useCloneState = (key: string, initialState: any): StatesProps => {
  // Stateがローカルストレージにあれば読み込み
  const localStorageState =
    localStorageAvailable() && window.localStorage.getItem(key)
  const [state, setState] = useState(
    localStorageState
      ? JSON.parse(localStorageState) ?? initialState
      : initialState
  )

  const wrappedSetState = (state: any): StatusProps => {
    state.version = QnFrontVersion
    // state代入元の参照を引き継がないようdeepcopyを使用
    setState(deepcopy(state))
    // Stateはローカルストレージに保存
    localStorageAvailable() &&
      window.localStorage.setItem(key, JSON.stringify(state))
    return success()
  }
  return {
    state,
    setState: wrappedSetState,
  }
}

/**
 * 型定義：Stateのhook
 */
export type StatesProps = {
  state: any
  setState: (state: any) => StatusProps
}

/**
 * 一定のタイミング後に動作させるhooks
 * @param interval 検知間隔
 * @returns 現在の日時
 */
export const useWatch = (interval: number): number => {
  const [time, updateTime] = useState(Date.now())

  useEffect(() => {
    const timeoutId = setTimeout(() => updateTime(Date.now()), interval)
    return (): void => {
      clearTimeout(timeoutId)
    }
  }, [interval, time])

  return time
}

/**
 * 一定間隔ごとにコールバック関数を動作させるhooks
 * @param callback コールバック関数
 * @param interval 待機間隔
 */
export const useInterval = (callback: () => void, interval: number): void => {
  const counter = useWatch(interval)

  useEffect(() => {
    callback()
  }, [callback, counter])
}

/**
 * チェック用のstateと専用にラップしたstate更新関数を返却する
 * @param initialState
 */
export const useCheckState = (
  initialState: CheckGroupProps
): [CheckGroupProps, (newState: CheckGroupProps['checks']) => void] => {
  const [state, setState] = useState(initialState)
  const wrappedSetState = useCallback((newState): void => {
    setState({
      ...state,
      checks: newState,
    })
  }, [])
  return [state, wrappedSetState]
}

/**
 * ラジオ用のstateと専用にラップしたstate更新関数を返却する
 * @param initialState
 */
export const useRadioState = (
  initialState: RadioGroupProps
): [RadioGroupProps, (newState: RadioGroupProps['radios']) => void] => {
  const [state, setState] = useState(initialState)
  const wrappedSetState = useCallback((newState): void => {
    setState({
      ...state,
      radios: newState,
    })
  }, [])
  return [state, wrappedSetState]
}

/**
 * スイッチ用のstateと専用にラップしたstate更新関数を返却する
 * @param initialState
 */
export const useSwitchState = (
  initialState: SwitchGroupProps
): [SwitchGroupProps, (newState: SwitchGroupProps['switch']) => void] => {
  const [state, setState] = useState(initialState)
  const wrappedSetState = useCallback((newState): void => {
    setState({
      ...state,
      switch: newState,
    })
  }, [])
  return [state, wrappedSetState]
}

/**
 * 四分割の破損箇所選択用のstateと専用にラップしたstate更新関数を返却する
 * @param initialState
 */
export const useDivideCarFourState = (
  initialState: DivideCarFourGroupProps
): [
  DivideCarFourGroupProps,
  (newState: DivideCarFourGroupProps['damagedPart']) => void
] => {
  const [state, setState] = useState(initialState)
  const wrappedSetState = useCallback((newState): void => {
    setState({
      ...state,
      damagedPart: newState,
    })
  }, [])
  return [state, wrappedSetState]
}

/**
 * テキストエリア用のstateと専用にラップしたstate更新関数を返却する
 * @param initialState
 */
export const useTextAreaState = (
  initialState: TextAreaGroupProps
): [TextAreaGroupProps, (newState: TextAreaGroupProps['text']) => void] => {
  const [state, setState] = useState(initialState)
  const wrappedSetState = useCallback((newState): void => {
    setState({
      ...state,
      text: newState,
    })
  }, [])
  return [state, wrappedSetState]
}

/**
 * 組み合わせ用のstateと専用にラップしたstate更新関数を返却する
 * @param initialState
 */
export const useCombinationState = (
  initialState: CombinationGroupProps
): [
  CombinationGroupProps,
  (newState: CombinationGroupProps['combination']) => void
] => {
  const [state, setState] = useState(initialState)
  const wrappedSetState = useCallback((newState): void => {
    setState({
      ...state,
      combination: newState,
    })
  }, [])
  return [state, wrappedSetState]
}

/**
 * 画像選択用のstateと専用にラップしたstate更新関数を返却する
 * @param initialState
 */
export const useImageState = (
  initialState: SelectImageGroupProps
): [
  SelectImageGroupProps,
  (newState: SelectImageGroupProps['images']) => void
] => {
  const [state, setState] = useState(initialState)
  const wrappedSetState = useCallback((newState): void => {
    setState({
      ...state,
      images: newState,
    })
  }, [])
  return [state, wrappedSetState]
}

/**
 * カレンダー用のstateと専用にラップしたstate更新関数を返却する
 * @param initialState
 */
// export const useCalendarState = (
//   initialState: CalendarGroupProps
// ): [CalendarGroupProps, (newState: CalendarGroupProps['calendar']) => void] => {
//   const [state, setState] = useState(initialState)
//   const wrappedSetState = useCallback((newState): void => {
//     setState({
//       ...state,
//       calendar: newState,
//     })
//   }, [])
//   return [state, wrappedSetState]
// }

/**
 * 一定時間後(デフォルトは3秒)に自動的に閉じるモーダルを動かす関数を返却する
 * @param setModal
 * @param timeout
 */
export const useAutoCloseModal: (
  setModal: (modalOpen: boolean) => void,
  timeout?: number
) => () => Promise<void> = (setModal, timeout) =>
  useCallback(async (): Promise<void> => {
    setModal(true)
    await new Promise((resolve) =>
      setTimeout(resolve, timeout || MODAL_DISPLAY_TIME)
    )
    setModal(false)
  }, [setModal, timeout])

/**
 * お車の状況のstate更新関数を返却する
 * @param newVehicleState
 * @param setState
 */
export const useVehicleStateCallback: (
  newVehicleState: string,
  setState: (newState: string) => void
) => () => StatusProps = (newVehicleState, setState) =>
  useCallback((): StatusProps => {
    setState(newVehicleState)
    return success()
  }, [newVehicleState, setState])

/**
 * 画面読み込み時、非削除が立っていなければEditingItemを削除する
 * @param notClearEditingItem 非削除フラグ
 */
export const useClearEditingItem: (notClearEditingItem?: boolean) => void = (
  notClearEditingItem
) => {
  const [, setEditingItem] = useContext(EditingItemContext)
  useEffect(() => {
    if (notClearEditingItem === false) {
      setEditingItem(initialQnEditState)
    }
  }, [])
}

/**
 * 型定義：編集中項目
 */
export type EditingItemProps = {
  notClearEditingItem?: boolean
}

export const useEditingItemStateKey = 'EditingItem'
export const useQNStateKey = 'QnState'

/**
 * localStorage用にラップしたEditingItemのStateのHooksを返却する
 * @param initialState 初期State
 * @returns StateのHooks
 */
export const useEditingItemState = (
  initialState: QnEditStateProps
): EditingItemContextProps => {
  const localStorageState =
    localStorageAvailable() &&
    window.localStorage.getItem(useEditingItemStateKey)
  const [state, setState] = useState<QnEditStateProps>(
    deepcopy(localStorageState ? JSON.parse(localStorageState) : initialState)
  )

  const wrappedSetState: React.Dispatch<any> = (state: QnEditStateProps) => {
    if (state) {
      setState(deepcopy(state))
      localStorageAvailable() &&
        window.localStorage.setItem(
          useEditingItemStateKey,
          JSON.stringify(state)
        )
    } else {
      setState(initialState)
      localStorageAvailable() &&
        window.localStorage.removeItem(useEditingItemStateKey)
    }
  }

  return [state, wrappedSetState]
}

export const useJwtStateKey = 'jwt'

/**
 * 編集用のコンテキスト取得と部分更新のSetterを生成するHooks
 * @returns 編集中の要請者情報、EditingItemContextのsetter、EditingItemContextの部分更新するコールバック
 */
export const useEditQnState = (): [
  QnEditStateProps,
  Dispatch<React.SetStateAction<QnEditStateProps>>,
  Dispatch<Partial<VehicleStateInfo>>
] => {
  const [editingItem, setEditingItem] = useContext(EditingItemContext)
  // コンテキストの部分更新をするコールバック
  const setState = useCallback(
    (value: Partial<VehicleStateInfo>) => {
      setEditingItem({
        ...editingItem,
        vehicleStateInfo: {
          ...editingItem.vehicleStateInfo,
          ...value,
        },
      })
    },
    [editingItem]
  )
  return [editingItem, setEditingItem, setState]
}

/**
 * localStorage用にラップしたJWTのStateのHooksを返却する
 * @param initialState 初期State
 * @returns StateのHooks
 */
export const useJwtState = (initialState: string): JwtContextProps => {
  const localStorageState =
    localStorageAvailable() && window.localStorage.getItem(useJwtStateKey)
  const [state, setState] = useState<string>(
    deepcopy(localStorageState ? JSON.parse(localStorageState) : initialState)
  )

  const wrappedSetState: React.Dispatch<string> = (state: string) => {
    setState(deepcopy(state))
    if (state) {
      localStorageAvailable() &&
        window.localStorage.setItem(useJwtStateKey, JSON.stringify(state))
    } else {
      localStorageAvailable()
    }
  }
  return [state, wrappedSetState]
}

const decodeJwt = (
  token: string
): {
  exp: number
  receptionNo: string
} => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  return JSON.parse(decodeURIComponent(escape(window.atob(base64))))
}

/**
 * jwtの設定の初期化を行う
 */
export const useJwt = (
  setIsJwtLoaded: React.Dispatch<React.SetStateAction<boolean>>
): void => {
  const [storageJwt, setStorageJwt] = useContext(JwtContext)

  useEffect(() => {
    // exp(有効期限)時刻の判定
    const decodedJwt = storageJwt ? decodeJwt(storageJwt) : ''
    if (
      !decodedJwt ||
      !decodedJwt.exp ||
      dayjs().isAfter(dayjs.unix(decodedJwt.exp))
    ) {
      setStorageJwt('')
    }
    setIsJwtLoaded(true)
  }, [])
}

/**
 * 起動時にQNステータスを取得する。また、QNステータスの監視を行い自動的な遷移を行う
 * @param states State
 */
export const useControlQNStatus = (
  qnStates: StatesProps,
  isJwtLoaded: boolean
): {
  apiCalled: boolean
  jwt: string
  isJwtLoaded: boolean
} => {
  const { state, setState } = qnStates
  const history = useHistory()
  const location = useLocation()
  const historyStackContext = useContext(HistoryStackContext)
  const [editItem, , setContactInfo] = useEditContractInfo()
  const [jwt, setJwt] = useContext(JwtContext)
  const [, setAPIKey] = useContext(APIKeyContext)
  // QNステータス取得APIが呼ばれたかどうか
  const [apiCalled, setApiCalled] = useState(false)
  // ページング履歴を1世代まで管理する
  const [pageHist, setPageHist] = useState<string[]>([])
  const [, getQnStatus] = useSender(API.QN_STATUS)
  const [error, setError] = useState<Error | null>()
  // websocket開始判定
  const [startWebSocket, setStartWebSocket] = useState(false)
  // websocket終了判定
  const [exitWebSocket, setExitWebSocket] = useState(false)

  useStartWebSocket(startWebSocket)
  useExitWebSocket(exitWebSocket)

  const transition = (pagePath: string, actionName?: string): void => {
    transitionWithAction(
      historyStackContext,
      actionName || 'QNステータスによる自動遷移',
      () => {
        history.push(pagePath)
      }
    )
  }

  // REASSIGNED, COMING_SOON, RESERVEDは位置共有画面内で自動遷移を行わないため、初回アクセス時のみ遷移を行う
  const landingMove = (qnStatus: string): void => {
    switch (qnStatus) {
      case QN_STATUS.REASSIGNED:
        transition(PAGE_PATH.RE_ASSIGNED)
        break
      case QN_STATUS.COMING_SOON:
        transition(PAGE_PATH.LOCATION_SHARING)
        break
      case QN_STATUS.RESERVED:
        transition(PAGE_PATH.RESERVED)
        break
      case QN_STATUS.FILLING:
        /**
         * SMSから取得したURLで再アクセスした場合は、fillingTransitionScreenPathで取得したページに遷移し、breakする
         * その他はリロードした場合の再着地で、react-routerが積み上げた履歴から最新の画面に遷移するので、breakして処理を抜ける、
         */
        fillingTransitionScreenPath && transition(fillingTransitionScreenPath)
        break
      default:
        break
    }
  }

  /** 着地したときのQNステータスがFILLINGの場合、画面遷移先のパスの文字列 */
  const fillingTransitionScreenPath = useMemo((): string => {
    /** 着地したURLのパラメータの文字列 */
    const landingUrlAccessParamsStr = new URLSearchParams(
      location.search
    ).toString()

    // 前回全く入力が行われていなかった場合なので、QN-02(スタート画面)のパスをreturnする
    if (JSON.stringify(editItem) === JSON.stringify(initialQnEditState)) {
      return PAGE_PATH.START
    }
    // SMSから送信されたURLを使ってQNに再アクセスした場合なので、QN-00(復帰画面)のパスをreturnする
    if (landingUrlAccessParamsStr) {
      return PAGE_PATH.RE_ACCESS
    }

    //  react-routerが積み上げた最新のページを表示するので、空文字をreturnする
    return ''
  }, [])

  // QNステータスの変更を起因に画面遷移を行う(REASSIGNED, COMING_SOON以外)
  const watchMove = (qnStatus: string): void => {
    switch (qnStatus) {
      case QN_STATUS.INFORMED:
        transition(PAGE_PATH.INFORMED)
        break
      case QN_STATUS.ASSIGNED:
        transition(PAGE_PATH.ASSIGNED)
        break
      case QN_STATUS.NOGUIDE:
        transition(PAGE_PATH.NO_GUIDE)
        break
      case QN_STATUS.IMPASSE:
        transition(PAGE_PATH.IMPASSE)
        break
      case QN_STATUS.DISPATCHING:
        transition(PAGE_PATH.LOCATION_SHARING)
        break
      case QN_STATUS.JOINED:
        transition(PAGE_PATH.JOINED)
        break
      case QN_STATUS.CLOSED:
        transition(PAGE_PATH.CLOSED)
        break
      case QN_STATUS.CANCELED:
        transition(PAGE_PATH.CANCELED)
        break
      default:
        break
    }
  }

  // 初回起動時の処理、jwt設定後1度だけ処理を行う。websocketの起動を行う
  useEffect(() => {
    if (jwt && isJwtLoaded) {
      // 確認ページから戻るときの制御のため、履歴を1世代まで管理する
      pageHist.unshift(history.location.pathname)
      setPageHist(pageHist)
      const unListen = history.listen((arg) => {
        pageHist.unshift(arg.pathname)
        pageHist.splice(2)
        setPageHist(pageHist)
      })
      // websocketの起動を試みる
      setStartWebSocket(true)
      return unListen
    }
  }, [])

  /** QNアクセス時のURLのクエリパラメータのキー */
  type LandingAccessKey = {
    [k in 'client' | 'from' | 'line' | 'params']?: string
  }
  // websocket起動後、着地URLアクセス処理を呼びGoogle Maps APIKeyとQNステータスの取得・それに対する画面遷移を行う
  useEffect(() => {
    /** 末端の画面に該当するQNステータスの一覧 */
    const excludedWebsocketConnectionList: Array<string> = [
      QN_STATUS.CANCELED,
      QN_STATUS.IMPASSE,
      QN_STATUS.JOINED,
      QN_STATUS.NOGUIDE,
    ]

    /** webSocket開始する対象フラグ
     * true：websocketを開始する
     * false：websocketを開始しない
     */
    const hasExcludeStartWebSocket = excludedWebsocketConnectionList.some(
      (excludedWebsocketConnection) =>
        excludedWebsocketConnection === state.qnStatus
    )

    if (hasExcludeStartWebSocket) {
      /**
       * 末端の画面からリロード等を行って再着地した場合は、以降の処理をせずに、画面の表示を行う
       */
      setApiCalled(true)
      setError(null)
      return
    }

    /** Websocket接続処理を行う。 */
    setStartWebSocket(true)

    /** バックエンド処理の呼び出しを行う。 */

    /**
     * A-2(着地URLアクセス)API呼び出し関数
     * @returns Promise<void>
     */
    const func = async (): Promise<void> => {
      try {
        const landingUrlAccessParams = new URLSearchParams(location.search)
        const urlParameters: LandingAccessKey = {
          client:
            landingUrlAccessParams.get('client') ||
            (state.urlParameters.client ?? ''),
          from:
            landingUrlAccessParams.get('from') ||
            (state.urlParameters.from ?? ''),
          line:
            landingUrlAccessParams.get('line') ||
            (state.urlParameters.line ?? ''),
          params:
            landingUrlAccessParams.get('params') ||
            (state.urlParameters.params ?? undefined),
        }

        if (
          CLIENT_SETTINGS.targetDomain === DOMAIN.ZN &&
          !urlParameters.client &&
          !urlParameters.from &&
          !urlParameters.line &&
          !urlParameters.params
        ) {
          urlParameters.client = 'zn'
          urlParameters.from = '9'
          urlParameters.line = 'direct'
        }

        if (
          !urlParameters.client ||
          !urlParameters.from ||
          !urlParameters.line
        ) {
          /** QNにアクセスしたURLのクエリパラメータに、client,from,lineの指定がなかった場合はエラー画面に遷移する */
          transition(PAGE_PATH.ERROR)
          return
        }

        /** バックエンドから取得した結果 */
        const result = await getQnStatus(urlParameters)

        const {
          jwt: restJwt,
          phoneNumber,
          contractorPhoneNumber,
          requesterName,
          contractorName,
          relationship,
          carNumber,
          carType,
          qnStatus: restQnStatus,
          errorCode,
          mapApiKey,
          locationSharingTimeout,
        } = result.data.data

        if (errorCode) {
          // バックエンドからの出力にerrorCodeが含まれている場合、エラー画面に遷移する
          setState({
            ...state,
            errorCode,
            urlParameters: {
              client: urlParameters.client,
              from: urlParameters.from,
              line: urlParameters.line,
              params: urlParameters.params,
            },
          })
          transition(
            PAGE_PATH.ERROR,
            'QNステータスAPIの取得に失敗によりエラー画面に遷移'
          )
          return
        }

        // 取得したGoogle Maps APIKeyを設定
        setAPIKey(mapApiKey)

        /** 表示画面を決定し、画面表示を行う。 */

        //  ローカルストレージにJWTの値がない場合、バックから取得したjwtを設定する
        if (!jwt) {
          // 新規にJWTが発行された場合はJWTをローカルストレージに保存する
          setJwt(restJwt)

          // 新規にJWTが発行された場合は、スタート画面の表示を行う。
          transition(
            PAGE_PATH.START,
            '新規にJWTが発行されたため、スタート画面に遷移'
          )
        } else {
          // フロント側で有効と判断したJWTがバックエンド側で有効期限切れやQNステータスが終了しているなどで無効と判断された場合にもう一度最初から処理を行うためアクセスURLのクエリーパラメータも必要となる。
          restJwt && setJwt(restJwt)

          // 有効なJWTが取得できた＝前回の続きから再開する場合、QNステータスとフロントのローカルストレージで管理する操作履歴を元に表示画面を決定し、画面表示を行う。
          landingMove(restQnStatus)
          watchMove(state.qnStatus)
        }

        const convertRelationShipType: Record<string, RelationshipTypes> = {
          '1': RelationshipTypes.self,
          '2': RelationshipTypes.family,
          '3': RelationshipTypes.friend,
          '6': RelationshipTypes.employee,
          '10': RelationshipTypes.other,
        }
        const convertVehicleType: Record<string, VehicleTypes> = isKsDomain()
          ? {
              '1': VehicleTypes.autoMobile,
              '2': VehicleTypes.truck,
              '3': VehicleTypes.truck,
              '4': VehicleTypes.truck,
              '5': VehicleTypes.truck,
              '6': VehicleTypes.truck,
              '7': VehicleTypes.bus,
              '8': VehicleTypes.motorbike,
              '9': VehicleTypes.motorbike,
              '10': VehicleTypes.motorbike,
              '11': VehicleTypes.motorbike,
              '12': VehicleTypes.motorbike,
              '99': VehicleTypes.other,
            }
          : isSaficDomain()
          ? {
              '1': VehicleTypes.autoMobile,
              '99': VehicleTypes.other,
            }
          : {
              [DOMAIN.SJ]: {
                '1': VehicleTypes.autoMobile,
                '2': VehicleTypes.truck,
                '3': VehicleTypes.truck,
                '4': VehicleTypes.truck,
                '5': VehicleTypes.truck,
                '6': VehicleTypes.truck,
                '7': VehicleTypes.bus,
                '8': VehicleTypes.motorbike,
                '9': VehicleTypes.motorbike,
                '10': VehicleTypes.motorbike,
                '11': VehicleTypes.motorbike,
                '12': VehicleTypes.motorbike,
                '99': VehicleTypes.other,
              },
            }[getDomain()]

        // QNステータスがFILLINGの場合、Websocket接続をクローズする。
        if (restQnStatus === QN_STATUS.FILLING) {
          // FILLINGで完了画面以降を開こうとした場合はスタート画面を開く
          if (
            [
              PAGE_PATH.INFORMED,
              PAGE_PATH.ASSIGNED,
              PAGE_PATH.LOCATION_SHARING,
              PAGE_PATH.JOINED,
              PAGE_PATH.NO_GUIDE,
              PAGE_PATH.RE_ASSIGNED,
              PAGE_PATH.IMPASSE,
              PAGE_PATH.CANCELED,
              PAGE_PATH.ERROR,
              PAGE_PATH.CLOSED,
              PAGE_PATH.RESERVED,
            ].includes(history.location.pathname)
          ) {
            transition(PAGE_PATH.START)
          }
          // QNステータスがFILLING以外の場合、バックエンド処理で取得した入力情報から、stateの情報を復元する。

          /** QNステータスを取得するAPIから契約者情報を取得し、editingItem.contactInfoにデータをsetする */
          setContactInfo({
            phoneNumber:
              editItem.contractInfo.phoneNumber ||
              (contractorPhoneNumber && contractorPhoneNumber.trim()
                ? contractorPhoneNumber
                : phoneNumber ?? ''),
            firstNumber:
              editItem.contractInfo.firstNumber ||
              sepalatePhoneNumber(
                contractorPhoneNumber && contractorPhoneNumber.trim()
                  ? contractorPhoneNumber
                  : phoneNumber ?? ''
              ).first,
            secondNumber:
              editItem.contractInfo.secondNumber ||
              sepalatePhoneNumber(
                contractorPhoneNumber && contractorPhoneNumber.trim()
                  ? contractorPhoneNumber
                  : phoneNumber ?? ''
              ).second,
            lastNumber:
              editItem.contractInfo.lastNumber ||
              sepalatePhoneNumber(
                contractorPhoneNumber && contractorPhoneNumber.trim()
                  ? contractorPhoneNumber
                  : phoneNumber ?? ''
              ).last,
            nameKana:
              editItem.contractInfo.nameKana ||
              convertFullKatakana(
                contractorName && contractorName.trim()
                  ? contractorName
                  : requesterName ?? ''
              ),
            contractorName:
              editItem.contractInfo.contractorName ||
              convertFullKatakana(contractorName ?? ''),
            lastName:
              editItem.contractInfo.lastName ||
              splitFullWidthName(
                contractorName && contractorName.trim()
                  ? contractorName
                  : requesterName ?? ''
              ).last,
            firstName:
              editItem.contractInfo.firstName ||
              splitFullWidthName(
                contractorName && contractorName.trim()
                  ? contractorName
                  : requesterName ?? ''
              ).first,
            relationship:
              editItem.contractInfo.relationship === RelationshipTypes['']
                ? convertRelationShipType[relationship] ?? RelationshipTypes['']
                : editItem.contractInfo.relationship,
            plateNumber:
              editItem.contractInfo.plateNumber ||
              splitFullWidthPlateNumber(carNumber ?? '').joinPlateNumber,
            motorbikeAndOtherPlateNumber:
              editItem.contractInfo.motorbikeAndOtherPlateNumber ||
              splitFullWidthPlateNumber(carNumber ?? '').joinPlateNumber,
            region:
              editItem.contractInfo.region ||
              splitFullWidthPlateNumber(carNumber ?? '').region,
            classificationNumber:
              editItem.contractInfo.classificationNumber ||
              splitFullWidthPlateNumber(carNumber ?? '').classNum,
            kana:
              editItem.contractInfo.kana ||
              splitFullWidthPlateNumber(carNumber ?? '').kana,
            designatedNumber:
              editItem.contractInfo.designatedNumber ||
              splitFullWidthPlateNumber(carNumber ?? '').design,
            vehicleType:
              editItem.contractInfo.vehicleType === VehicleTypes['']
                ? convertVehicleType[carType] ?? VehicleTypes['']
                : editItem.contractInfo.vehicleType,
          })
          setExitWebSocket(true)
        }
        /** qnStateをQNステータスを取得するAPIから取得した値に更新する */
        setState({
          ...state,
          qnStatus: restQnStatus,
          locationSharingTimeout,
          urlParameters: {
            client: urlParameters.client,
            from: urlParameters.from,
            line: urlParameters.line,
            params: urlParameters.params,
          },
          initialContractInfo: {
            phoneNumber:
              contractorPhoneNumber && contractorPhoneNumber.trim()
                ? contractorPhoneNumber
                : phoneNumber ?? '',
            firstNumber: sepalatePhoneNumber(
              contractorPhoneNumber && contractorPhoneNumber.trim()
                ? contractorPhoneNumber
                : phoneNumber ?? ''
            ).first,
            secondNumber: sepalatePhoneNumber(
              contractorPhoneNumber && contractorPhoneNumber.trim()
                ? contractorPhoneNumber
                : phoneNumber ?? ''
            ).second,
            lastNumber: sepalatePhoneNumber(
              contractorPhoneNumber && contractorPhoneNumber.trim()
                ? contractorPhoneNumber
                : phoneNumber ?? ''
            ).last,
            nameKana: convertFullKatakana(
              contractorName && contractorName.trim()
                ? contractorName
                : requesterName ?? ''
            ),
            contractorName: convertFullKatakana(contractorName ?? ''),
            lastName: splitFullWidthName(
              contractorName && contractorName.trim()
                ? contractorName
                : requesterName ?? ''
            ).last,
            firstName: splitFullWidthName(
              contractorName && contractorName.trim()
                ? contractorName
                : requesterName ?? ''
            ).first,
            relationship:
              convertRelationShipType[relationship] ?? RelationshipTypes[''],
            plateNumber: splitFullWidthPlateNumber(carNumber ?? '')
              .joinPlateNumber,
            motorbikeAndOtherPlateNumber: splitFullWidthPlateNumber(
              carNumber ?? ''
            ).joinPlateNumber,
            region: splitFullWidthPlateNumber(carNumber ?? '').region,
            classificationNumber: splitFullWidthPlateNumber(carNumber ?? '')
              .classNum,
            kana: splitFullWidthPlateNumber(carNumber ?? '').kana,
            designatedNumber: splitFullWidthPlateNumber(carNumber ?? '').design,
            vehicleType: convertVehicleType[carType] ?? VehicleTypes[''],
          },
        })
        setApiCalled(true)
        setError(null)
      } catch (e) {
        setError(new Error(e))
      }
    }
    func()
  }, [])

  // QNステータスの変更を起因に起動する。初回QNステータス取得の後に動作開始する
  useEffect(() => {
    if (apiCalled) {
      // QNステータス変更による自動的な画面遷移を行う。
      watchMove(state.qnStatus)
    }
  }, [apiCalled, state.qnStatus])

  // 非同期中のエラーの処理
  useEffect(() => {
    if (error) {
      throw error
    }
  }, [error])

  useEffect(() => {
    if (apiCalled) {
      // ブラウザバックの制御
      const controlBack = (): void => {
        // QNステータスがFILLING(要請者入力中)以外の場合、要請者の入力が完了しデータが伝送しているため、入力する画面に戻らないように制御する
        if (state.qnStatus !== QN_STATUS.FILLING) {
          landingMove(state.qnStatus)
          watchMove(state.qnStatus)
        }
      }

      window.addEventListener('popstate', controlBack)
      return (): void => {
        window.removeEventListener('popstate', controlBack)
      }
    }
  }, [apiCalled, qnStates.state.qnStatus, pageHist])

  return { apiCalled, jwt, isJwtLoaded }
}

/**
 * jwtから取得した受付番号を返却する
 * @returns 受付番号
 */
export const useReceptionNo = (): string => {
  const [jwt] = useContext(JwtContext)

  return useMemo(() => {
    if (jwt) {
      const tokenObject = decodeJwt(jwt)
      return tokenObject.receptionNo
    } else {
      return ''
    }
  }, [jwt])
}

export function useEditContractInfo(): [
  QnEditStateProps,
  React.Dispatch<React.SetStateAction<QnEditStateProps>>,
  React.Dispatch<Partial<ContractInfo>>
] {
  const [editingItem, setEditingItem] = useContext(EditingItemContext)

  // コンテキストの部分更新をするコールバック
  const setState = useCallback(
    (value: Partial<ContractInfo>) => {
      setEditingItem({
        ...editingItem,
        contractInfo: {
          ...editingItem.contractInfo,
          ...value,
        },
      })
    },
    [editingItem]
  )

  return [editingItem, setEditingItem, setState]
}

export function useEditLocationState(): [
  QnEditStateProps,
  React.Dispatch<React.SetStateAction<QnEditStateProps>>,
  React.Dispatch<Partial<LocationStateInfo>>
] {
  const [editingItem, setEditingItem] = useContext(EditingItemContext)
  // コンテキストの部分更新をするコールバック
  const setState = useCallback(
    (value: Partial<LocationStateInfo>) => {
      setEditingItem({
        ...editingItem,
        locationStateInfo: {
          ...editingItem.locationStateInfo,
          ...value,
        },
      })
    },
    [editingItem]
  )

  return [editingItem, setEditingItem, setState]
}

const ProgressTracker: {
  [key: string]: number | undefined
} = {
  ['/']: undefined,
  ['/trouble-place']: 1,
  ['/trouble-place/surrounding']: 2,
  ['/trouble-place/info/vehicle-state']: 3,
  ['/trouble-place/info/vehicle-state/engine-trouble']: 3,
  ['/trouble-place/info/vehicle-state/flat-tire']: 3,
  ['/trouble-place/info/vehicle-state/accident']: 3,
  ['/trouble-place/info/vehicle-state/falling-wheel']: 3,
  ['/trouble-place/info/vehicle-state/empty-fuel']: 3,
  ['/trouble-place/info/vehicle-state/loss-key']: 3,
  ['/trouble-place/info/vehicle-state/others']: 3,
  ['/trouble-place/info/vehicle-state/transport-destination']: 3,
  ['/service-other-empty']: undefined,
  ['/service-empty']: undefined,
  ['/confirmation']: 5,
  ['/informed']: undefined,
  ['/assigned']: undefined,
  ['/location-sharing']: undefined,
  ['/joined']: undefined,
  ['/no-guide']: undefined,
  ['/re-assigned']: undefined,
  ['/impasse']: undefined,
  ['/canceled']: undefined,
  ['/error']: undefined,
  ['/closed']: undefined,
  ['/special-contract']: undefined,
  ['/reservation']: undefined,
  ['/reserved']: undefined,
  ['/service-information']: 4,
  ['/contract-information']: 4,
  ['/re-access']: undefined,
}

/**
 * 画面のパスからプログレストラッカーの点灯位置をnumberで返却するHooks
 * @returns プログレストラッカーの点灯位置
 */
export function useProgressPoint(): number | undefined {
  const location = useLocation()
  const [progressPoint, setProgressPoint] = useState<number | undefined>()
  useEffect(() => {
    setProgressPoint(ProgressTracker[location.pathname] ?? undefined)
  }, [])
  return progressPoint
}

/**
 * 末端の画面に遷移したときに実行するHooks
 * ・webSocket切断
 * ・ローカルストレージの中身を初期化
 * 末端の画面遷移時に離脱時のパスをローカルストレージにセットしている処理と
 * バッティングしてしまうため、離脱時のパスを設定した後に、本処理を実行する
 * @param qnStatesProps QnStatesProps
 */
export function useServiceEndProcess(qnStatesProps: QnStatesProps): void {
  useExitWebSocket(true) // webSocket切断

  useEffect(() => {
    const generalParameter: URLParameters = {
      client:
        getKsClientLowerCase() !== undefined
          ? getKsClientLowerCase()
          : isSaficDomain()
          ? 'sn'
          : {
              [DOMAIN.SJ]: 'sjr',
            }[getDomain()],
      from: '9',
      line: 'direct',
    }
    // サービス終了したためローカルストレージを削除する
    qnStatesProps.setState({
      ...QnState,
      qnStatus: QN_STATUS.FILLING,
      urlParameters: generalParameter,
    })
    localStorage.removeItem(useEditingItemStateKey)
    localStorage.removeItem(useJwtStateKey)
  }, [qnStatesProps.state.currentInputPagePath])
}

/**
 * 事故発生場所と搬送先の郵便番号を除いた住所が
 * 一致していた場合に、エラー表示とボタンを非活性にする処理
 * @param editState QnEditStateProps
 */

export const useAddressMatch = (editState: QnEditStateProps): boolean => {
  const [isError, setIsError] = useState<boolean>(false)
  // --- 住所のアドレスを成形 ---
  const addressFormat = (address: string): string => {
    return address
      .replace(/〒/g, '')
      .replace(/^[0-9０-９]{3}-?[0-9０-９]{4}/g, '')
      .trim()
  }
  // --- ユーザーが選択した住所の重複チェック ---
  const addressMatchCheck = (): void => {
    const locationData = JSON.parse(localStorage.getItem('QnState') as string)
    if (!locationData) return
    localStorage.setItem('QnState', JSON.stringify(locationData))
    const location = locationData.location.address
    const destinationLocation = editState.destinationLocation.address
    if (addressFormat(location) === addressFormat(destinationLocation)) {
      setIsError(true)
    } else {
      setIsError(false)
    }
  }
  // --- 依存する値が変更された時に addressMatchCheck を呼び出す ---
  useEffect(() => {
    addressMatchCheck()
  }, [editState.vehicleStateInfo, editState.destinationLocation])
  return isError
}
